import { addCalendarDays, nextDeparture, stationDateTime } from "../services/journeyTime";
// ─── Trip Context ─────────────────────────────────────────────────────────────
// Provides trip planner state + persistence to all components via useTrip().
// Orchestrates useTripPlanner, useTripPersistence, usePOIData, and useSavedTrips.

import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef, type ReactNode } from "react";
import type { City, TrainRoute, TripLeg, DaySegment, StopActions } from "../data/types";
import type { POI } from "../services/poiService";
import { loadTripByShareToken, type SavedTripSummary } from "../services/storageService";
import { useTripPlanner } from "../hooks/useTripPlanner";
import { useTripPersistence, loadPersistedTrip } from "../hooks/useTripPersistence";
import { usePOIData } from "../hooks/usePOIData";
import { useSavedTrips } from "../hooks/useSavedTrips";
import { registerCities } from "../services/cityStore";
import { decodeTripFromUrl } from "../services/tripUrlCodec";
import { toast } from "sonner";

// ─── Context Shape ──────────────────────────────────────────────────────

export interface TripContextValue {
  // Trip state
  tripStops: City[];
  tripName: string;
  tripPanelVisible: boolean;
  tripLegs: TripLeg[];
  mapTripLegs: TripLeg[];
  tripDaySegments: DaySegment[];
  tripStationDayMap: Map<string, { color: string; day: number }>;
  staleLegKeys: Map<string, string>;
  stopActions: StopActions;
  travelDate: string;
  selectedStopDate: string;
  selectedStopHasOnwardConnection: boolean;
  pendingConnection: { fromId: string; toId: string; reason: "overnight" | "stop" } | null;
  getConnectionDeparture: (from: City) => { customDate?: string; customTime?: string };

  // Selected stop (shared across search + map + discovery card)
  selectedStop: City | null;
  cardPosition: { x: number; y: number } | null;

  // Hovered POI (React-managed overlay replacing Leaflet tooltip)
  hoveredPOI: POI | null;
  hoveredPOIPosition: { x: number; y: number } | null;

  // Explore mode data
  allPOIs: POI[];
  /** Unique POIs near trip stops (derived from poiConnections) */
  tripPOIs: POI[];
  poiConnections: Array<{ from: [number, number]; to: [number, number]; poi: POI }>;
  discoveryValue: number;

  // Trip mutations
  setTripStops: React.Dispatch<React.SetStateAction<City[]>>;
  setTripName: React.Dispatch<React.SetStateAction<string>>;
  setTripPanelVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setTripLegs: React.Dispatch<React.SetStateAction<TripLeg[]>>;
  setStopActions: React.Dispatch<React.SetStateAction<StopActions>>;
  setTravelDate: React.Dispatch<React.SetStateAction<string>>;
  setSelectedStop: React.Dispatch<React.SetStateAction<City | null>>;
  setCardPosition: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;

  // Hovered POI actions
  setHoveredPOI: (poi: POI, pos: { x: number; y: number }) => void;
  clearHoveredPOI: () => void;

  // Trip handlers
  handleAddToTrip: (station: City, fromStation?: City | null) => void;
  handleAddRouteToTrip: (route: TrainRoute, fromStation?: City | null, overrideFromId?: string, overrideToId?: string) => void;
  handleRemoveFromTrip: (index: number) => void;
  handleReorderStop: (index: number, direction: "up" | "down") => void;
  handleSetNights: (stationId: string, nights: number) => void;
  handleClearTrip: () => void;
  handleCloseTripPanel: () => void;
  handleToggleTripPanel: () => void;
  handleTripStopClick: (station: City) => void;
  handleExportTrip: () => void;
  handleToggleFood: (selectedStop: City, fromStation?: City | null) => { dest?: City; customDate?: string; customTime?: string } | void;
  handleToggleOvernight: (selectedStop: City, fromStation?: City | null) => { dest?: City; customDate?: string; customTime?: string } | void;
  handleToggleFika: (selectedStop: City, fromStation?: City | null) => { dest?: City; customDate?: string; customTime?: string } | void;
  handleContinueFrom: (station: City, fromStation?: City | null) => void;

  // UI helpers
  selectStopWithPosition: (station: City, pos: { x: number; y: number }) => void;
  updateCardPosition: (pos: { x: number; y: number } | null) => void;
  deselectStop: () => void;

  // Saved trips (cloud)
  savedTrips: SavedTripSummary[];
  savedTripsLoading: boolean;
  activeTripId: string | null;
  activeShareToken: string | null;
  refreshSavedTrips: () => Promise<void>;
  saveCurrentTrip: () => Promise<string | null>;
  saveAsNewTrip: () => Promise<void>;
  loadSavedTrip: (tripId: string) => Promise<void>;
  deleteSavedTrip: (tripId: string) => Promise<void>;
  renameSavedTrip: (tripId: string, newName: string) => Promise<void>;
}

const TripContext = createContext<TripContextValue | null>(null);

// ─── Provider ───────────────────────────────────────────────────────────

/** Zero-pad a number to 2 digits */
const pad = (n: number) => String(n).padStart(2, "0");

export function TripProvider({ children }: Readonly<{ children: ReactNode }>) {
  // Shared UI state
  const [selectedStop, setSelectedStop] = useState<City | null>(null);
  const [cardPosition, setCardPosition] = useState<{ x: number; y: number } | null>(null);
  const [stopActions, setStopActions] = useState<StopActions>({});

  // Hovered POI state (for React-managed overlay)
  const [hoveredPOIState, setHoveredPOIState] = useState<POI | null>(null);
  const [hoveredPOIPosition, setHoveredPOIPosition] = useState<{ x: number; y: number } | null>(null);

  // Initialize with tomorrow's date (lazy initializer — runs once)
  const [travelDate, setTravelDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return `${tomorrow.getFullYear()}-${pad(tomorrow.getMonth() + 1)}-${pad(tomorrow.getDate())}`;
  });

  // Trip planner hook (includes ghost legs internally)
  const trip = useTripPlanner(travelDate, stopActions, setStopActions, setSelectedStop);

  // Delegate POI data to dedicated hook
  const poi = usePOIData(trip.tripStops);

  // Delegate cloud CRUD to dedicated hook
  const cloud = useSavedTrips(
    {
      tripStops: trip.tripStops,
      tripName: trip.tripName,
      tripLegs: trip.tripLegs,
      setTripStops: trip.setTripStops,
      setTripName: trip.setTripName,
      setTripLegs: trip.setTripLegs,
      setTripPanelVisible: trip.setTripPanelVisible,
    },
    travelDate,
    stopActions,
    setTravelDate,
    setStopActions,
  );

  const restoreSource = useRef(globalThis.location.href);
  const restoreGeneration = useRef(0);
  const stopsRef = useRef(trip.tripStops);
  stopsRef.current = trip.tripStops;
  const handleClearTrip = useCallback(() => {
    restoreGeneration.current++;
    cloud.detachActiveTrip();
    trip.handleClearTrip();
  }, [cloud.detachActiveTrip, trip.handleClearTrip]);

  const handleRemoveFromTrip = useCallback((index: number) => {
    if (trip.tripStops.length === 1 && index === 0) handleClearTrip();
    else trip.handleRemoveFromTrip(index);
  }, [trip.tripStops.length, trip.handleRemoveFromTrip, handleClearTrip]);

  // ─── Restore trip from URL or localStorage ────────────────────
  useEffect(() => {
    let cancelled = false;
    const generation = restoreGeneration.current;
    const params = new URL(restoreSource.current).searchParams;
    const isShared = params.has("share") || params.has("trip") || params.has("tripv2");
    if (isShared) cloud.detachActiveTrip();
    void (async () => {
      try {
        let snapshot;
        if (params.has("share")) {
          const loaded = await loadTripByShareToken(params.get("share")!);
          if (!loaded) throw new Error("Shared trip not found.");
          snapshot = { stops: loaded.stops, tripName: loaded.name, travelDate: loaded.travelDate ?? travelDate,
            stopActions: loaded.stopActions, legs: loaded.legs };
        } else if (isShared) {
          snapshot = await decodeTripFromUrl(restoreSource.current);
        } else {
          const loaded = loadPersistedTrip();
          snapshot = loaded && { stops: loaded.tripStops, tripName: loaded.tripName, travelDate: loaded.travelDate,
            stopActions: loaded.stopActions, legs: loaded.tripLegs };
        }
        if (!snapshot || cancelled || generation !== restoreGeneration.current || (isShared && stopsRef.current.length > 0)) return;
        registerCities(snapshot.stops);
        trip.setTripStops(snapshot.stops);
        trip.setTripName(snapshot.tripName);
        trip.setTripLegs(snapshot.legs);
        setTravelDate(snapshot.travelDate);
        setStopActions(snapshot.stopActions);
        trip.setTripPanelVisible(snapshot.stops.length > 0);
        if (isShared) globalThis.history.replaceState({}, "", globalThis.location.pathname);
      } catch (error) {
        if (!cancelled) toast.error(error instanceof Error ? error.message : "Could not load this trip.");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-save
  useTripPersistence(trip.tripStops, stopActions, trip.tripName, travelDate, trip.tripLegs);

  // ─── UI helpers ───────────────────────────────────────────────

  const selectStopWithPosition = useCallback(
    (station: City, pos: { x: number; y: number }) => {
      setSelectedStop(station);
      setCardPosition(pos);
    },
    []
  );

  const updateCardPosition = useCallback(
    (pos: { x: number; y: number } | null) => {
      setCardPosition(pos);
    },
    []
  );

  const deselectStop = useCallback(() => {
    setSelectedStop(null);
    setCardPosition(null);
  }, []);

  const setHoveredPOI = useCallback(
    (p: POI, pos: { x: number; y: number }) => {
      setHoveredPOIState(p);
      setHoveredPOIPosition(pos);
    },
    []
  );

  const clearHoveredPOI = useCallback(() => {
    setHoveredPOIState(null);
    setHoveredPOIPosition(null);
  }, []);

  // ─── Context value ───────────────────────────────────────────
  // Memoised so consumers only re-render when actual data changes,
  // not on every TripProvider render. Setters / handlers from
  // useState / useCallback are stable references and excluded from deps.

  const selectedArrival = trip.mapTripLegs.find(leg => leg.toStationId === selectedStop?.id)?.arrivalDate;
  const selectedDay = selectedStop ? trip.tripStationDayMap.get(selectedStop.id)?.day ?? 1 : 1;
  const selectedStopDate = selectedArrival || addCalendarDays(travelDate, selectedDay - 1);
  const selectedStopHasOnwardConnection = !!selectedStop && trip.tripLegs.some(leg =>
    leg.toStationId !== selectedStop.id &&
    (leg.fromStationId === selectedStop.id || leg.stations.includes(selectedStop.id)));
  const pendingConnection = useMemo(() => {
    const leg = trip.tripStops.flatMap((stop, i) => trip.mapTripLegs.filter(l =>
      l.requiresSelection && l.fromStationId === stop.id && l.toStationId === trip.tripStops[i + 1]?.id))[0];
    if (!leg) return null;
    return { fromId: leg.fromStationId, toId: leg.toStationId,
      reason: stopActions[leg.fromStationId]?.overnight ? "overnight" as const : "stop" as const };
  }, [trip.tripStops, trip.mapTripLegs, stopActions]);
  const getConnectionDeparture = useCallback((from: City) => {
    const previous = trip.mapTripLegs.find(l => l.toStationId === from.id);
    const plannedDate = addCalendarDays(travelDate, (trip.tripStationDayMap.get(from.id)?.day ?? 1) - 1);
    const local = stationDateTime(nextDeparture(from, previous, stopActions, plannedDate), from);
    return { customDate: local.date, customTime: local.time };
  }, [trip.mapTripLegs, trip.tripStationDayMap, stopActions, travelDate]);

  const value: TripContextValue = useMemo(() => ({
    tripStops: trip.tripStops,
    tripName: trip.tripName,
    tripPanelVisible: trip.tripPanelVisible,
    tripLegs: trip.tripLegs,
    mapTripLegs: trip.mapTripLegs,
    tripDaySegments: trip.tripDaySegments,
    tripStationDayMap: trip.tripStationDayMap,
    staleLegKeys: trip.staleLegKeys,
    stopActions,
    travelDate,
    selectedStopDate,
    selectedStopHasOnwardConnection,
    pendingConnection,
    getConnectionDeparture,
    selectedStop,
    cardPosition,
    hoveredPOI: hoveredPOIState,
    hoveredPOIPosition,
    allPOIs: poi.allPOIs,
    tripPOIs: poi.tripPOIs,
    poiConnections: poi.poiConnections,
    discoveryValue: poi.discoveryValue,

    setTripStops: trip.setTripStops,
    setTripName: trip.setTripName,
    setTripPanelVisible: trip.setTripPanelVisible,
    setTripLegs: trip.setTripLegs,
    setStopActions,
    setTravelDate,
    setSelectedStop,
    setCardPosition,

    setHoveredPOI,
    clearHoveredPOI,

    handleAddToTrip: trip.handleAddToTrip,
    handleAddRouteToTrip: trip.handleAddRouteToTrip,
    handleRemoveFromTrip,
    handleReorderStop: trip.handleReorderStop,
    handleSetNights: trip.handleSetNights,
    handleClearTrip,
    handleCloseTripPanel: trip.handleCloseTripPanel,
    handleToggleTripPanel: trip.handleToggleTripPanel,
    handleTripStopClick: trip.handleTripStopClick,
    handleExportTrip: trip.handleExportTrip,
    handleToggleFood: trip.handleToggleFood,
    handleToggleOvernight: trip.handleToggleOvernight,
    handleToggleFika: trip.handleToggleFika,
    handleContinueFrom: trip.handleContinueFrom,

    selectStopWithPosition,
    updateCardPosition,
    deselectStop,

    savedTrips: cloud.savedTrips,
    savedTripsLoading: cloud.savedTripsLoading,
    activeTripId: cloud.activeTripId,
    activeShareToken: cloud.activeShareToken,
    refreshSavedTrips: cloud.refreshSavedTrips,
    saveCurrentTrip: cloud.saveCurrentTrip,
    saveAsNewTrip: cloud.saveAsNewTrip,
    loadSavedTrip: cloud.loadSavedTrip,
    deleteSavedTrip: cloud.deleteSavedTrip,
    renameSavedTrip: cloud.renameSavedTrip,
  }), [
    // Data that changes
    trip.tripStops, trip.tripName, trip.tripPanelVisible, trip.tripLegs, trip.mapTripLegs,
    trip.tripDaySegments, trip.tripStationDayMap, trip.staleLegKeys,
    stopActions, travelDate, selectedStopDate, selectedStopHasOnwardConnection, pendingConnection, getConnectionDeparture, selectedStop, cardPosition,
    hoveredPOIState, hoveredPOIPosition,
    poi.allPOIs, poi.tripPOIs, poi.poiConnections, poi.discoveryValue,
    cloud.savedTrips, cloud.savedTripsLoading, cloud.activeTripId, cloud.activeShareToken,
    // Handlers (stable via useCallback, but listed for correctness)
    trip.handleAddToTrip, trip.handleAddRouteToTrip, handleRemoveFromTrip, trip.handleReorderStop, trip.handleSetNights,
    handleClearTrip, trip.handleCloseTripPanel, trip.handleToggleTripPanel,
    trip.handleTripStopClick, trip.handleExportTrip,
    trip.handleToggleFood, trip.handleToggleOvernight, trip.handleToggleFika,
    trip.handleContinueFrom,
    selectStopWithPosition, updateCardPosition, deselectStop,
    setHoveredPOI, clearHoveredPOI,
    cloud.refreshSavedTrips, cloud.saveCurrentTrip, cloud.saveAsNewTrip, cloud.loadSavedTrip, cloud.deleteSavedTrip, cloud.renameSavedTrip,
  ]);

  return <TripContext.Provider value={value}>{children}</TripContext.Provider>;
}

// ─── Hook ───────────────────────────────────────────────────────────────

export function useTrip(): TripContextValue {
  const ctx = useContext(TripContext);
  if (!ctx) throw new Error("useTrip must be used within <TripProvider>");
  return ctx;
}
